import { useEffect, useRef } from 'react'
import { googlePlaceIds } from 'src/atoms/compare'
import { googleSearchString, internalGooglePlaceId, selectedLocationId } from 'src/atoms/search'
import { MutableSnapshot, RecoilState, useRecoilCallback, useRecoilSnapshot, useRecoilValue } from 'recoil'
import { useParsedRouter } from 'src/layout/hooks/use-parsed-router'
import { ParsedUrlQueryInput } from 'querystring'
import { useMapExplorerNavigation } from 'src/views/map-explorer/sidebar/search/use-navigation'
// import { circle, polygon } from 'src/atoms/custom-area'

export enum ParamType {
  BOOLEAN = 'boolean',
  STRING = 'string',
  STRING_ARRAY = 'string_array',
  JSON = 'json',
}

type StringParamsDefinition = Record<string, { state: RecoilState<any>; type: ParamType }>

type QueryInputValue =
  | string
  | number
  | boolean
  | readonly string[]
  | readonly number[]
  | readonly boolean[]
  | null
  | undefined

const STRING_PARAMS: StringParamsDefinition = {
  'id.am-loc': {
    state: selectedLocationId,
    type: ParamType.STRING,
  },
  'id.google': {
    state: internalGooglePlaceId,
    type: ParamType.STRING,
  },
  q: {
    state: googleSearchString,
    type: ParamType.STRING,
  },
  cpids: {
    state: googlePlaceIds,
    type: ParamType.STRING_ARRAY,
  },
  // polygon: {
  //   state: polygon,
  //   type: ParamType.JSON,
  // },
  // circle: {
  //   state: circle,
  //   type: ParamType.JSON,
  // },
}

export const APP_SECTIONS_ROUTES = {
  mapExplorer: ['/?', '/insights'],
  admin: ['/admin/users'],
  reports: ['/reports'],
  tenantFinder: ['/tenant-finder'],
  mapBuilder: ['/map-builder'],
  upgrade: ['/upgrade'],
  ownerReveal: ['/owner-reveal'],
  signup: ['/signup', '/signup/billing', '/signup/confirmation'],
  helpCenter: ['/help-center'],
}
export const APP_PATHNAMES = {
  mapExplorer: '/[[...url]]',
  admin: '/admin/users/[[...url]]',
  tenantFinder: '/tenant-finder',
  upgrade: '/[[...url]]',
  signup: '/signup',
  ownerReveal: '/owner-reveal/[[...url]]',
}

const toBooleanState = (value: QueryInputValue) => {
  if (Array.isArray(value)) {
    return value[0] == null ? false : true
  }
  return value == null ? false : true
}

const toStringState = (value: QueryInputValue) => {
  if (Array.isArray(value)) {
    return value[0] == null ? undefined : value[0]
  }
  return value == null ? undefined : value
}

const toStringArrayState = (value: QueryInputValue): string[] => {
  if (value === null || value === undefined) {
    return []
  }
  if (Array.isArray(value)) {
    return value.filter((v) => v != null) as string[]
  }
  return value.toString().split(',')
}

const assertNever = (type: never): never => {
  throw new Error('this should never happen')
}

const toState = (type: ParamType, value: QueryInputValue) => {
  switch (type) {
    case ParamType.BOOLEAN:
      return toBooleanState(value)
    case ParamType.STRING:
      return toStringState(value)
    case ParamType.STRING_ARRAY:
      return toStringArrayState(value)
    case ParamType.JSON:
      if (Array.isArray(value)) {
        return value[0] == null ? undefined : toStringState(JSON.parse(value[0]))
      }
      return value == null ? undefined : toStringState(JSON.parse(value.toString()))
    default:
      assertNever(type)
  }
}

const toBooleanUrlParam = (value: unknown) => {
  if (value === true) {
    return 'true'
  } else {
    return null
  }
}

const toStringUrlParam = (value: unknown) => {
  const parsedValue = value as string
  if (value !== '' || value !== undefined) {
    return parsedValue
  } else {
    return null
  }
}

const toStringArrayUrlParam = (value: unknown) => {
  const parsedValue = value as string[]
  if (parsedValue.length) {
    return parsedValue.join(',')
  } else {
    return null
  }
}

const toUrlParam = (type: ParamType, value: unknown) => {
  switch (type) {
    case ParamType.BOOLEAN:
      return toBooleanUrlParam(value)
    case ParamType.STRING:
      return toStringUrlParam(value)
    case ParamType.STRING_ARRAY:
      return toStringArrayUrlParam(value)
    case ParamType.JSON:
      return toStringUrlParam(JSON.stringify(value))
    default:
      assertNever(type)
  }
}

export const urlInitializer = (snapshot: MutableSnapshot, query: ParsedUrlQueryInput) => {
  for (const [key, atom] of Object.entries(STRING_PARAMS)) {
    const val = query[key]
    if (val) {
      const parsedValue = toState(atom.type, val)
      if (parsedValue !== null) {
        snapshot.set(atom.state, parsedValue)
      }
    }
  }
}

// TODO(catriel): Check if we need this inverse sync
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const useSyncURLtoState = () => {
  return useRecoilCallback(({ snapshot, set }) => (query: ParsedUrlQueryInput) => {
    for (const [key, atom] of Object.entries(STRING_PARAMS)) {
      const atomValue = snapshot.getLoadable(atom.state).valueMaybe()
      const queryRawValue = query[key]
      const queryParsedValue = queryRawValue ? toState(atom.type, queryRawValue) : undefined
      if (!atomValue && queryParsedValue) {
        set(atom.state, queryParsedValue)
      }
    }
  })
}

const useSyncStateToURL = () => {
  return useRecoilCallback(({ snapshot, set }) => (query: ParsedUrlQueryInput) => {
    const newQuery = { ...query }
    let changed = false
    for (const [key, atom] of Object.entries(STRING_PARAMS)) {
      const atomValue = snapshot.getLoadable(atom.state).valueMaybe()
      const atomURLValue = atomValue ? toUrlParam(atom.type, atomValue) : undefined
      const queryRawValue = query[key]

      if (atomURLValue != queryRawValue) {
        if (!atomURLValue) {
          newQuery[key] = undefined
        } else {
          newQuery[key] = atomURLValue
        }
        changed = true
      }
    }
    return { newQuery, changed }
  })
}

// Legacy old code isnt used anywhere
export const URLSync = () => {
  const { router, query, hash } = useParsedRouter()
  const snapshot = useRecoilSnapshot()
  const syncStateToURL = useSyncStateToURL()

  // AlphaMap Location to Google Place ID
  const loading = useRef(false)
  const { selectLocation } = useMapExplorerNavigation()
  const googlePlaceId = useRecoilValue(internalGooglePlaceId)

  const resync = async () => {
    const { newQuery, changed } = syncStateToURL(query)
    if (changed) {
      const joinedQuery = {
        ...router.query,
        ...newQuery,
      }
      const finalQuery = Object.keys(joinedQuery).reduce((acc: ParsedUrlQueryInput, key) => {
        if (joinedQuery[key] !== undefined) {
          acc[key] = joinedQuery[key]
        }
        return acc
      }, {})
      try {
        await router.push({
          pathname: router.pathname,
          query: finalQuery,
          hash,
        })
      } catch (e) {
        console.error(e)
      }
    }
  }

  const load = async () => {
    if (loading.current) {
      return
    }
    if (query['id.google'] === undefined && googlePlaceId === undefined && query['id.am-loc'] !== undefined) {
      loading.current = true
      await selectLocation(query['id.am-loc'] as string)
      loading.current = false
    }
  }

  useEffect(() => {
    void resync()
  }, [snapshot])

  useEffect(() => {
    void load()
  }, [query['id.google'], query['id.am-loc']])

  return null
}
